import type {
  ApplicationDetail,
  ApplicationPortfolioView,
  ApplicationRow,
  GlobalFilters,
  QueryResult,
} from '@aurora-pvi/contracts';
import { ROUTES } from '@aurora-pvi/contracts';
import type { Application } from '@aurora-pvi/seed';
import { PAGE_SOURCES, type RepositoryContext, canEdit, resolved } from '../context';
import { applyFilters } from '../filters';
import type { ApplicationRefinement } from '../types';
import { buildMetadata, currencyMetric, metricValue, moneyOrNull } from '../metadata';
import { applicationRef, ownerRef, productRef, provenanceBadge, simpleRef } from '../refs';

/**
 * Application portfolio (ref: 06_Application_to_Product_Mapping.png).
 *
 * `annualRunCost` is the masking test case: for a caller without finance
 * entitlement it comes back `null`, the cost KPIs come back `Unavailable`, and
 * `meta.scope.maskedFields` names the field. It never comes back as zero, which
 * would quietly tell the reader the estate is free.
 */

export function createApplicationRepository(context: RepositoryContext) {
  const { dataset, index, user } = context;

  const productCountByApp = new Map<string, number>();
  const processCountByApp = new Map<string, number>();
  for (const link of dataset.productApplicationLinks) {
    productCountByApp.set(
      link.applicationCode,
      (productCountByApp.get(link.applicationCode) ?? 0) + 1,
    );
  }
  for (const link of dataset.applicationProcessLinks) {
    processCountByApp.set(
      link.applicationCode,
      (processCountByApp.get(link.applicationCode) ?? 0) + 1,
    );
  }

  const toRow = (application: Application): ApplicationRow => ({
    id: application.id,
    businessCode: application.businessCode,
    name: application.name,
    domain: application.domain,
    businessCapability: application.businessCapability,
    owner: ownerRef(index, user, application.ownerPersonCode),
    criticality: application.criticality,
    technicalHealth: application.technicalHealth,
    technicalHealthScore: application.technicalHealthScore,
    lifecycle: application.lifecycle,
    isDuplicateCandidate: application.isDuplicateCandidate,
    // The fixture's Application carries no vendor. Null reads as "in house",
    // which is the honest answer for a record that never named a supplier.
    vendorName: null,
    annualRunCost: moneyOrNull(user, application.annualRunCostCents),
    // The fixture records one run-cost figure and no capitalisation split, so
    // these are null rather than a guess derived from it — the estate is where
    // budget lives, and a fabricated split here would disagree with it.
    capexBudget: null,
    capexActual: null,
    opexBudget: null,
    opexActual: null,
    productCount: productCountByApp.get(application.businessCode) ?? 0,
    processCount: processCountByApp.get(application.businessCode) ?? 0,
    isOrphan: (productCountByApp.get(application.businessCode) ?? 0) === 0,
    provenance: provenanceBadge(application.provenance),
  });

  return {
    async list(
      filters: GlobalFilters,
      page: { page: number; pageSize: number },
      selectedId?: string,
      // Accepted and ignored: this repository has no HTTP route behind it, and
      // the screen no longer refines rows itself. Named in the signature so the
      // two implementations of the port stay the same shape.
      _refinement?: ApplicationRefinement,
    ): Promise<QueryResult<ApplicationPortfolioView>> {
      const scope = applyFilters(dataset, index, user, filters);

      // Orphans belong to nobody, so they are only visible at enterprise scope.
      // A department leader should not be handed 140 applications that are not
      // theirs; the enterprise view is where that gap has to be answered.
      const candidates = scope.isEnterpriseScope
        ? dataset.applications
        : dataset.applications.filter((a) => scope.applicationCodes.has(a.businessCode));

      const search = (filters.search ?? '').trim().toLowerCase();
      const lifecycleSet = new Set(filters.lifecycle ?? []);
      const healthSet = new Set(filters.health ?? []);

      const filtered = candidates.filter((application) => {
        if (lifecycleSet.size > 0 && !lifecycleSet.has(application.lifecycle)) return false;
        if (healthSet.size > 0 && !healthSet.has(application.technicalHealth)) return false;
        if (
          search &&
          !`${application.name} ${application.businessCode} ${application.businessCapability}`
            .toLowerCase()
            .includes(search)
        ) {
          return false;
        }
        return true;
      });

      const orphans = filtered.filter((a) => (productCountByApp.get(a.businessCode) ?? 0) === 0);
      const duplicates = filtered.filter((a) => a.isDuplicateCandidate);
      const inProduction = filtered.filter((a) => a.lifecycle === 'Active');
      const candidatesCount = filtered.filter(
        (a) => a.lifecycle === 'Candidate' || a.lifecycle === 'Proposed',
      ).length;
      const runCostCents = filtered.reduce((sum, a) => sum + a.annualRunCostCents, 0);
      const debtCents = filtered.reduce((sum, a) => sum + a.technicalDebtCents, 0);

      const start = (page.page - 1) * page.pageSize;
      const rows = filtered.slice(start, start + page.pageSize).map(toRow);

      const selected =
        (selectedId
          ? filtered.find((a) => a.id === selectedId || a.businessCode === selectedId)
          : undefined) ?? filtered[0];

      const detail: ApplicationDetail | null = selected
        ? {
            row: toRow(selected),
            version: selected.version,
            vendor:
              dataset.suppliers.find((s) => s.businessCode === selected.vendorCode)?.name ??
              'Aurora internal',
            hostingEnvironment: selected.hostingEnvironment,
            architecture: selected.architecture,
            technologyStack: selected.technologyStack,
            database: selected.database,
            dataSensitivity: selected.dataSensitivity,
            securityScore: selected.securityScore,
            availability30d: selected.availability30d,
            integrationCount: selected.integrationCount,
            totalCostOfOwnership: moneyOrNull(user, selected.totalCostOfOwnershipCents),
            // Null, not an all-Unclassified composition. The fixture has no
            // cost-nature dimension to read and inventing one would put a
            // breakdown on screen that no feed produced.
            costComposition: null,
            // Same reasoning as the composition above, and the same answer.
            // The fixture holds no contract, no service and no data asset;
            // producing an empty commitment or an empty service list would
            // claim the estate had been checked and found to have none.
            contractedCommitment: null,
            services: [],
            dataAssets: [],
            lifecycleSince: selected.lifecycleSince,
            nextReview: selected.nextReview,
            businessValue: selected.businessValue.map((entry) => ({
              dimension: entry.dimension,
              rating: entry.rating,
            })),
            duplicate:
              selected.duplicateOfCodes.length > 0 && selected.duplicateOverlapScore !== null
                ? {
                    overlapScore: selected.duplicateOverlapScore,
                    duplicateOf: selected.duplicateOfCodes
                      .map((code) => index.applicationsByCode.get(code))
                      .filter((a): a is Application => a !== undefined)
                      .map(applicationRef),
                  }
                : null,
            // Neither decider nor decision date survives the seed, so both are
            // stated as unrecorded rather than credited to the loader.
            recordedDisposition:
              selected.disposition && selected.dispositionConfidence !== null
                ? {
                    disposition: selected.disposition,
                    rationale: selected.dispositionRationale ?? '',
                    confidence: selected.dispositionConfidence,
                    decidedAt: null,
                    decidedBy: null,
                  }
                : null,
            relatedProducts: dataset.productApplicationLinks
              .filter((link) => link.applicationCode === selected.businessCode)
              .map((link) => {
                const product = index.productsByCode.get(link.productCode);
                if (!product) return null;
                return {
                  product: productRef(product),
                  role: link.role,
                  allocationPercent: link.allocationPercent,
                };
              })
              .filter((entry): entry is NonNullable<typeof entry> => entry !== null),
            relatedProcesses: dataset.applicationProcessLinks
              .filter((link) => link.applicationCode === selected.businessCode)
              .slice(0, 25)
              .map((link) => {
                const process = dataset.processes.find((p) => p.businessCode === link.processCode);
                if (!process) return null;
                return {
                  process: simpleRef(process.id, 'Process', process.businessCode, process.name),
                  role: link.role,
                };
              })
              .filter((entry): entry is NonNullable<typeof entry> => entry !== null),
          }
        : null;

      const facetCounts = (
        field: string,
        label: string,
        pick: (a: Application) => string,
      ): ApplicationPortfolioView['facets'][number] => {
        const counts = new Map<string, number>();
        for (const application of filtered) {
          const value = pick(application);
          counts.set(value, (counts.get(value) ?? 0) + 1);
        }
        return {
          field,
          label,
          options: Array.from(counts.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([value, count]) => ({ value, label: value, count })),
        };
      };

      const view: ApplicationPortfolioView = {
        kpis: [
          metricValue({
            dataset,
            metricId: 'MET-040',
            label: 'Applications',
            value: filtered.length,
            unit: 'count',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-041',
            label: 'In Production',
            value: inProduction.length,
            unit: 'count',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-042',
            label: 'Candidates',
            value: candidatesCount,
            unit: 'count',
            confidence: 'Medium',
          }),
          metricValue({
            dataset,
            metricId: 'MET-043',
            label: 'Orphan Applications',
            value: orphans.length,
            unit: 'count',
            desiredDirection: 'Down',
            confidence: 'High',
            drilldowns: [
              {
                dimension: 'orphans',
                label: 'Mapping studio',
                route: ROUTES.mappingApplicationProduct,
              },
            ],
          }),
          metricValue({
            dataset,
            metricId: 'MET-044',
            label: 'Duplicate Capabilities',
            value: duplicates.length,
            unit: 'count',
            desiredDirection: 'Down',
            confidence: 'Medium',
          }),
          currencyMetric({
            dataset,
            user,
            metricId: 'MET-045',
            label: 'Technical Debt (Est.)',
            cents: debtCents,
            desiredDirection: 'Down',
            confidence: 'Low',
          }),
          currencyMetric({
            dataset,
            user,
            metricId: 'MET-046',
            label: 'Annual Run Cost',
            cents: runCostCents,
            desiredDirection: 'Down',
            confidence: 'Medium',
          }),
        ],
        page: {
          rows,
          page: page.page,
          pageSize: page.pageSize,
          totalRows: filtered.length,
          hasMore: start + page.pageSize < filtered.length,
        },
        facets: [
          facetCounts('domain', 'Domain', (a) => a.domain),
          facetCounts('businessCapability', 'Business capability', (a) => a.businessCapability),
          facetCounts('criticality', 'Business criticality', (a) => a.criticality),
          facetCounts('technicalHealth', 'Technical health', (a) => a.technicalHealth),
          facetCounts('lifecycle', 'Lifecycle status', (a) => a.lifecycleStatusLabel),
          facetCounts('hostingEnvironment', 'Hosting', (a) => a.hostingEnvironment),
          facetCounts('duplicate', 'Duplicate status', (a) =>
            a.isDuplicateCandidate ? 'Potential duplicate' : 'No overlap identified',
          ),
        ],
        selectedApplicationId: selected?.id ?? null,
        detail,
      };

      return resolved({
        data: view,
        meta: buildMetadata(dataset, user, scope, {
          route: ROUTES.applications,
          sources: PAGE_SOURCES.applications,
          confidence: 'Medium',
          sequence: page.page,
          actions: [
            { action: 'export', permission: 'export', enabled: true },
            { action: 'addApplication', permission: 'create', enabled: canEdit(user) },
            {
              action: 'proposeDisposition',
              permission: 'propose',
              enabled: canEdit(user),
            },
          ],
        }),
      });
    },
  };
}
